import React, { useCallback, useState } from 'react';
import { Alert, AlertColor, Snackbar } from '@mui/material';

interface ToastState {
  message: string;
  severity: AlertColor;
}

/**
 * Minimal snackbar helper. Render `toast` once in the page and call
 * `show(message, severity)` from anywhere in it.
 */
export function useToast() {
  // `open` and the content are separate so the message stays in place while
  // the snackbar animates out, instead of flashing an empty box.
  const [state, setState] = useState<ToastState | null>(null);
  const [open, setOpen] = useState(false);

  const show = useCallback((message: string, severity: AlertColor = 'success') => {
    setState({ message, severity });
    setOpen(true);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  const toast = (
    <Snackbar
      open={open && Boolean(state)}
      autoHideDuration={4000}
      onClose={close}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={close} severity={state?.severity ?? 'info'} variant="filled" sx={{ width: '100%' }}>
        {state?.message}
      </Alert>
    </Snackbar>
  );

  return { show, toast };
}
